import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { EmptyState } from '@/components/app/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { humanizeError } from '@/lib/apiClient';
import { funnelObservabilityApi } from '../api/funnelObservabilityApi';

export function UserModelsPage() {
  const [pendingUserId, setPendingUserId] = React.useState('');
  const [userId, setUserId] = React.useState('');

  const q = useQuery({
    queryKey: ['ai-user-models', userId],
    queryFn: () => funnelObservabilityApi.getUserModels(userId),
    enabled: !!userId,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="User models"
        description="Universal AI model + per-goal journey models. Maintained by UserModelService and JourneyModelService."
      />

      <Card>
        <CardContent className="flex items-end gap-3 p-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label htmlFor="user-id-input">User ID</Label>
            <Input
              id="user-id-input"
              value={pendingUserId}
              onChange={(e) => setPendingUserId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setUserId(pendingUserId.trim())}
              placeholder="Paste a userId..."
            />
          </div>
          <Button onClick={() => setUserId(pendingUserId.trim())}>Load</Button>
        </CardContent>
      </Card>

      {!userId ? (
        <EmptyState
          title="Enter a user ID"
          description="View the user's universal traits and per-goal journey models."
        />
      ) : q.isLoading ? (
        <LoadingState />
      ) : q.isError || !q.data ? (
        <ErrorState
          title="User models failed"
          description={humanizeError(q.error)}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Universal AI model</CardTitle>
            </CardHeader>
            <CardContent>
              {q.data.universalAiModel ? (
                <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs">
                  {JSON.stringify(q.data.universalAiModel, null, 2)}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground">Not yet populated.</p>
              )}
            </CardContent>
          </Card>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold">
              Journey models ({q.data.journeyModels.length})
            </h2>
            {q.data.journeyModels.length === 0 ? (
              <Card>
                <CardContent className="py-6 text-sm text-muted-foreground">
                  No goals.
                </CardContent>
              </Card>
            ) : (
              q.data.journeyModels.map((g) => (
                <Card key={g.goalId}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                      {g.title || g.goalId}
                      {g.isDeleted && (
                        <Badge variant="muted" className="text-[10px]">
                          deleted
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {g.category ?? 'no category'} · <code>{g.goalId}</code>
                    </p>
                  </CardHeader>
                  <CardContent>
                    {g.journeyModel ? (
                      <div className="space-y-1.5 text-sm">
                        <div>
                          <span className="text-xs text-muted-foreground">Archetype: </span>
                          {g.journeyModel.archetype ?? '—'}
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Readiness: </span>
                          {g.journeyModel.readinessStage ?? '—'}
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Self-efficacy: </span>
                          {g.journeyModel.domainSelfEfficacy ?? '—'}
                        </div>
                        {g.journeyModel.identityClaim && (
                          <div>
                            <span className="text-xs text-muted-foreground">Identity: </span>
                            <span className="italic">{g.journeyModel.identityClaim}</span>
                          </div>
                        )}
                        {g.journeyModel.fieldConfidence && (
                          <div className="text-xs text-muted-foreground">
                            confidence:{' '}
                            {Object.entries(g.journeyModel.fieldConfidence)
                              .map(([k, v]) => `${k} ${v.toFixed(2)}`)
                              .join(' · ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Not yet populated.</p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
